/**
 * Admin Command Center route definitions.
 *
 * Route hierarchy follows the Command Center structure:
 *   /admin/dashboard              — Command Center Overview
 *   /admin/operations/*           — KYC, Farmers, Documents, Requests, Alerts
 *   /admin/insurance/*            — Policies, Claims, Analytics, Ledger
 *   /admin/credit/*               — Applications, Escrow, Exposure, Settlements
 *   /admin/marketplace/*          — Vendors, Hatcheries, Ledger
 *   /admin/system/*               — Integrations, Email, RBAC, Monitoring
 *
 * Each route carries:
 *   - `role`       — legacy role(s) for backward-compat fallback
 *   - `permission` — primary RBAC permission check(s)
 *   - `layout`     — 'command' to use AdminCommandLayout (default for admin routes)
 */
import React from 'react';

// ── Existing pages ───────────────────────────────────────────
import AdminDashboard from './pages/AdminDashboard';
import CommandCenterDashboard from './pages/CommandCenterDashboard';
import KYCReview from './pages/KYCReview';
import AlertsManagement from './pages/AlertsManagement';
import UserManagement from './pages/UserManagement';
import DocumentVerification from './pages/DocumentVerification';
import RequestManagement from './pages/RequestManagement';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import UserRoleDistribution from './pages/UserRoleDistribution';
import UserMonitoringDashboard from './pages/UserMonitoringDashboard';
import EmailSettings from './pages/EmailSettings';
import APISettings from './pages/APISettings';
import UserSettings from './pages/UserSettings';
import SettingsDashboard from './pages/SettingsDashboard';
import WeatherSettings from './pages/WeatherSettings';
import { CreditControlCockpit } from './credit';
import { InsuranceRiskDashboard } from './insurance/risk-engine';

// ── New placeholder pages ────────────────────────────────────
import PolicyLedgerPage from './pages/PolicyLedgerPage';
import EscrowAccountsPage from './pages/EscrowAccountsPage';
import ExposureOverviewPage from './pages/ExposureOverviewPage';
import SettlementsPage from './pages/SettlementsPage';
import VendorManagementPage from './pages/VendorManagementPage';
import HatcheryRegistryPage from './pages/HatcheryRegistryPage';
import VendorLedgerPage from './pages/VendorLedgerPage';
import SMSGatewayPage from './pages/SMSGatewayPage';
import PaymentGatewayPage from './pages/PaymentGatewayPage';
import RBACManagementPage from './pages/RBACManagementPage';

const adminRoutes = [
  // ═══════════════════════════════════════════════════════════
  // DASHBOARD — Command Center Overview
  // ═══════════════════════════════════════════════════════════
  {
    path: '/admin/dashboard',
    element: <CommandCenterDashboard />,
    role: ['admin', 'aic_officer'],
    permission: ['system.analytics', 'insurance.view', 'operations.kyc.view'],
    layout: 'command',
  },

  // ═══════════════════════════════════════════════════════════
  // OPERATIONS
  // ═══════════════════════════════════════════════════════════
  {
    path: '/admin/operations/kyc',
    element: <KYCReview />,
    role: ['admin', 'aic_officer'],
    permission: ['operations.kyc.view', 'operations.kyc.approve'],
    layout: 'command',
  },
  {
    path: '/admin/operations/farmers',
    element: <UserManagement />,
    role: ['admin', 'aic_officer'],
    permission: ['system.users.view', 'system.users.manage'],
    layout: 'command',
  },
  {
    path: '/admin/operations/documents',
    element: <DocumentVerification />,
    role: ['admin', 'aic_officer'],
    permission: 'operations.documents',
    layout: 'command',
  },
  {
    path: '/admin/operations/requests',
    element: <RequestManagement />,
    role: ['admin', 'aic_officer'],
    permission: ['operations.leads.view', 'operations.documents'],
    layout: 'command',
  },
  {
    path: '/admin/operations/alerts',
    element: <AlertsManagement />,
    role: ['admin', 'aic_officer'],
    permission: 'system.notifications',
    layout: 'command',
  },

  // ═══════════════════════════════════════════════════════════
  // INSURANCE CONTROL
  // ═══════════════════════════════════════════════════════════
  {
    path: '/admin/insurance/policies',
    element: <AdminDashboard />,
    role: ['admin', 'aic_officer'],
    permission: ['insurance.view', 'insurance.manage'],
    layout: 'command',
  },
  {
    path: '/admin/insurance/claims',
    element: <AdminDashboard />,
    role: ['admin', 'aic_officer'],
    permission: ['insurance.claims.view', 'insurance.claims.process'],
    layout: 'command',
  },
  {
    path: '/admin/insurance/analytics',
    element: <InsuranceRiskDashboard />,
    role: ['admin', 'aic_officer'],
    permission: ['insurance.view', 'insurance.manage'],
    layout: 'command',
  },
  {
    path: '/admin/insurance/ledger',
    element: <PolicyLedgerPage />,
    role: ['admin', 'aic_officer'],
    permission: ['insurance.view', 'insurance.manage'],
    layout: 'command',
  },

  // ═══════════════════════════════════════════════════════════
  // CREDIT CONTROL
  // ═══════════════════════════════════════════════════════════
  {
    path: '/admin/credit/applications',
    element: <CreditControlCockpit />,
    role: ['admin', 'finance_manager'],
    permission: ['credit.view', 'credit.manage'],
    layout: 'command',
  },
  {
    path: '/admin/credit/escrow',
    element: <EscrowAccountsPage />,
    role: ['admin', 'finance_manager'],
    permission: ['credit.escrow.view', 'credit.escrow.manage'],
    layout: 'command',
  },
  {
    path: '/admin/credit/exposure',
    element: <ExposureOverviewPage />,
    role: ['admin', 'finance_manager'],
    permission: ['credit.view', 'credit.manage'],
    layout: 'command',
  },
  {
    path: '/admin/credit/settlements',
    element: <SettlementsPage />,
    role: ['admin', 'finance_manager'],
    permission: ['credit.settlement.view', 'credit.settlement.manage'],
    layout: 'command',
  },

  // ═══════════════════════════════════════════════════════════
  // MARKETPLACE
  // ═══════════════════════════════════════════════════════════
  {
    path: '/admin/marketplace/vendors',
    element: <VendorManagementPage />,
    role: ['admin'],
    permission: ['marketplace.view', 'marketplace.manage'],
    layout: 'command',
  },
  {
    path: '/admin/marketplace/hatcheries',
    element: <HatcheryRegistryPage />,
    role: ['admin'],
    permission: ['marketplace.view', 'marketplace.manage'],
    layout: 'command',
  },
  {
    path: '/admin/marketplace/ledger',
    element: <VendorLedgerPage />,
    role: ['admin'],
    permission: ['marketplace.view', 'marketplace.manage'],
    layout: 'command',
  },

  // ═══════════════════════════════════════════════════════════
  // SYSTEM CONTROL
  // ═══════════════════════════════════════════════════════════
  {
    path: '/admin/system/integrations/weather',
    element: <WeatherSettings />,
    role: ['admin', 'aic_officer'],
    permission: 'system.settings',
    layout: 'command',
  },
  {
    path: '/admin/system/integrations/sms',
    element: <SMSGatewayPage />,
    role: ['admin'],
    permission: ['system.settings', 'system.sms'],
    layout: 'command',
  },
  {
    path: '/admin/system/integrations/payment',
    element: <PaymentGatewayPage />,
    role: ['admin'],
    permission: 'system.settings',
    layout: 'command',
  },
  {
    path: '/admin/system/email',
    element: <EmailSettings />,
    role: ['admin', 'aic_officer'],
    permission: 'system.settings',
    layout: 'command',
  },
  {
    path: '/admin/system/rbac',
    element: <RBACManagementPage />,
    role: ['admin'],
    permission: ['system.roles.manage', 'system.roles.assign'],
    layout: 'command',
  },
  {
    path: '/admin/system/monitoring',
    element: <UserMonitoringDashboard />,
    role: ['admin', 'aic_officer'],
    permission: ['system.users.view', 'system.analytics'],
    layout: 'command',
  },

  // ═══════════════════════════════════════════════════════════
  // LEGACY ROUTES — redirect compatibility
  // These keep old URLs working while the sidebar uses new paths.
  // ═══════════════════════════════════════════════════════════
  { path: '/admin/users',                  element: <UserManagement />,           role: ['admin', 'aic_officer'],          permission: ['system.users.view', 'system.users.manage'],         layout: 'command' },
  { path: '/admin/kyc',                    element: <KYCReview />,                role: ['admin', 'aic_officer'],          permission: ['operations.kyc.view', 'operations.kyc.approve'],    layout: 'command' },
  { path: '/admin/documents',              element: <DocumentVerification />,     role: ['admin', 'aic_officer'],          permission: 'operations.documents',                               layout: 'command' },
  { path: '/admin/alerts',                 element: <AlertsManagement />,         role: ['admin', 'aic_officer'],          permission: 'system.notifications',                               layout: 'command' },
  { path: '/admin/requests',               element: <RequestManagement />,        role: ['admin', 'aic_officer'],          permission: ['operations.leads.view', 'operations.documents'],    layout: 'command' },
  { path: '/admin/analytics',              element: <AnalyticsDashboard />,       role: ['admin', 'aic_officer'],          permission: 'system.analytics',                                   layout: 'command' },
  { path: '/admin/credit',                 element: <CreditControlCockpit />,     role: ['admin', 'finance_manager'],      permission: ['credit.view', 'credit.manage'],                     layout: 'command' },
  { path: '/admin/insurance/risk-engine',  element: <InsuranceRiskDashboard />,   role: ['admin', 'aic_officer'],          permission: ['insurance.view', 'insurance.manage'],               layout: 'command' },
  { path: '/admin/policies',               element: <AdminDashboard />,           role: ['admin', 'aic_officer'],          permission: ['insurance.view', 'insurance.manage'],               layout: 'command' },
  { path: '/admin/claims',                 element: <AdminDashboard />,           role: ['admin', 'aic_officer'],          permission: ['insurance.claims.view', 'insurance.claims.process'], layout: 'command' },
  { path: '/admin/user-role-distribution', element: <UserRoleDistribution />,     role: ['admin', 'agent', 'aic_officer'], permission: 'system.analytics',                                   layout: 'command' },
  { path: '/admin/user-monitoring',        element: <UserMonitoringDashboard />,  role: ['admin', 'agent', 'aic_officer'], permission: ['system.users.view', 'system.analytics'],            layout: 'command' },
  { path: '/admin/settings',               element: <SettingsDashboard />,        role: ['admin', 'aic_officer'],          permission: 'system.settings',                                    layout: 'command' },
  { path: '/admin/settings/email',         element: <EmailSettings />,            role: ['admin', 'aic_officer'],          permission: 'system.settings',                                    layout: 'command' },
  { path: '/admin/settings/api',           element: <APISettings />,              role: ['admin', 'aic_officer'],          permission: 'system.settings',                                    layout: 'command' },
  { path: '/admin/settings/weather',       element: <WeatherSettings />,          role: ['admin', 'aic_officer'],          permission: 'system.settings',                                    layout: 'command' },
  { path: '/admin/settings/users',         element: <UserSettings />,             role: ['admin', 'aic_officer'],          permission: 'system.users.manage',                                layout: 'command' },
];

export default adminRoutes;
